import {
  Box, Button, Card, CardContent, TextField, Typography, Grid, ButtonGroup,
  CircularProgress, Divider, ListItemText, Checkbox, FormControl, Select, MenuItem, Stack
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import React, { useState, ReactElement, useEffect, useCallback, useMemo } from 'react'
import stylesheet from '../detail-container.stylesheet'
import ColorBubbleComponent from './color-bubble.component'
import { PatientService } from '../../../../services/patient.service'
import { toast } from 'react-toastify'
import Helper from '../../../../utils/helper'
import { useCurrentUser } from '../../../../utils/use-current-user'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../redux/store'
import moment from 'moment'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'

interface IProps {
  entityRef: string
  input: any
  leftHeader: string
  rightHeader: string | ReactElement
  programs?: string[]
  subHeader?: string | ReactElement
  closeOverride: any
  onOverrideSubmit: any
  classes: ClassNameMap
}

const ManualOverrideComponent: React.FC<IProps> = ({
  entityRef,
  input,
  leftHeader,
  rightHeader,
  programs = [],
  subHeader,
  closeOverride,
  onOverrideSubmit,
  classes,
}) => {
  const [note, setNote] = useState('Clinician aware')
  const [loading, setLoading] = useState(false)
  const [overrideConfirm, setOverrideConfirm] = useState(false)
  const [resolveOverride, setResolveOverride] = useState(false)
  const [overridenPrograms, setOverridenPrograms] = useState<string[]>([])
  const [resolveOverridenPrg, setResolveOverridenPrg] = useState<string[]>([])
  const [prevOverridenPrograms, setPrevOverridenPrograms] = useState<string[]>([])
  const [refData, setRefData] = useState<any>({})

  const patientService = useMemo(() => new PatientService(), [])
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const { currentRole } = useCurrentUser()
  const { updatePatientData } = useUpdateDataAction(patientDetail?.id)

  useEffect(() => {
    if (input?.overridden_programs?.length >= 1) {
      setPrevOverridenPrograms(input.overridden_programs)
      setResolveOverridenPrg(input.overridden_programs)
    }

    if (programs?.length === 1) {
      setOverridenPrograms(programs)
    }

    getOverrideNotes()
  }, [])

  const getOverrideNotes = useCallback(() => {
    if (!patientDetail?.id) return

    patientService.getOverrideNote(patientDetail.id, entityRef, input.id)
      .then((res: any) => {
        setLoading(false)
        setRefData(res?.data?.override_notes || {})
      })
      .catch((error: any) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }, [patientDetail?.id, entityRef, input.id, patientService])

  const onCloseOverride = useCallback(() => {
    closeOverride(input.id)
  }, [closeOverride, input.id])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value)
  }, [])

  const handleOverrideNotes = useCallback(() => {
    setResolveOverride(true)
    setOverrideConfirm(false)
  }, [])

  const getPrgName = useCallback((key: any) => {
    const prgObj = patientDetail?.program_enrollments?.find((f: { program: any }) => f.program === key)
    return prgObj?.program_display_name || ''
  }, [patientDetail?.program_enrollments])

  const handleAddOverride = useCallback(() => {
    setLoading(true)
    const payload = {
      entity_ref: entityRef,
      entity_id: input.id,
      note: note,
      programs: overridenPrograms,
      author_role: currentRole
    }

    patientService
      .postOverrideValue(payload)
      .then((res: any) => {
        setLoading(false)
        updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
        onOverrideSubmit(input.id)
      })
      .catch((error: any) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }, [entityRef, input.id, note, overridenPrograms, currentRole, patientService, updatePatientData, onOverrideSubmit])

  const handleRemoveOverride = useCallback(() => {
    setLoading(true)
    const payload = {
      entity_ref: entityRef,
      entity_id: input.id,
      programs: resolveOverridenPrg
    }

    patientService.removeOverride(patientDetail?.id, payload)
      .then((res: any) => {
        setLoading(false)
        if (res?.data?.message === 'success') {
          updatePatientData({ isMeasurement: true, isTreatments: true, isConditions: true })
          onOverrideSubmit(input.id)
        }
      })
      .catch((error: any) => {
        setLoading(false)
        toast.error(Helper.getErrorText(error), Helper.bottom_center())
      })
  }, [entityRef, input.id, resolveOverridenPrg, patientDetail?.id, patientService, updatePatientData, onOverrideSubmit])

  const onConfirm = useCallback(() => {
    if (overrideConfirm) {
      handleAddOverride()
    } else if (resolveOverride) {
      handleRemoveOverride()
    }
  }, [overrideConfirm, resolveOverride, handleAddOverride, handleRemoveOverride])

  const handleChangeForPrograms = useCallback((event: any, isResolve = false) => {
    const { target: { value } } = event
    const prgList = typeof value === 'string' ? value.split(',') : value

    if (isResolve) {
      setResolveOverridenPrg(prgList)
    } else {
      setOverridenPrograms(prgList)
    }
  }, [])

  const getPrgLen = useCallback((data: any) => {
    const prgLen = data?.program ? 1 : (data?.programs?.length || 0)
    return input?.overridden_programs?.length === prgLen
  }, [input?.overridden_programs?.length])

  const isAddOverrideDisabled = useMemo(() => {
    return getPrgLen(input)
  }, [getPrgLen, input])

  const isRemoveOverrideDisabled = useMemo(() => {
    const inputPrograms = input?.programs || input?.program
    return !input?.overridden_programs?.some((r: any) => inputPrograms?.includes(r))
  }, [input])

  const isSubmitDisabled = useMemo(() => {
    if (overrideConfirm) {
      return note === '' || overridenPrograms.length <= 0
    }
    if (resolveOverride) {
      return resolveOverridenPrg?.length <= 0
    }
    return true
  }, [overrideConfirm, resolveOverride, note, overridenPrograms.length, resolveOverridenPrg?.length])

  const handleAddOverrideClick = useCallback(() => {
    if (!isAddOverrideDisabled) {
      setOverrideConfirm(true)
      setResolveOverride(false)
    }
  }, [isAddOverrideDisabled])

  const handleRemoveOverrideClick = useCallback(() => {
    if (!isRemoveOverrideDisabled) {
      handleOverrideNotes()
    }
  }, [isRemoveOverrideDisabled, handleOverrideNotes])

  return (
    <Box mb={1} className={classes.customCard}>
      <Card variant="outlined" className={classes.fullWidth}>
        <CardContent sx={{ padding: '0.5rem 0.8rem 0.5rem 0.8rem' }}>
          <Grid container xs={12} direction="row" justifyContent="space-between" alignItems="center" className={classes.initialFlexwrap}>
            <Grid item>
              <Typography variant="subtitle2" className={classes.subHeadername}>
                {leftHeader}
                <ColorBubbleComponent
                  selectedProgramList={programs}
                  overridenProgramList={input?.overridden_programs}
                />
              </Typography>
            </Grid>
            <Grid item className={classes.subTitle}>
              <Typography align="right">
                {rightHeader}
              </Typography>
            </Grid>
          </Grid>

          {subHeader && (
            <Grid container xs={12} direction="row" alignItems="baseline" className={classes.initialFlexwrap}>
              <Grid item className={classes.subHeaderTitle}>
                <Typography color="textSecondary" variant="subtitle2">
                  {subHeader}
                </Typography>
              </Grid>
            </Grid>
          )}

          <Divider />

          {loading ? (
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Box p={1}>
                <CircularProgress size="2rem" />
              </Box>
            </Grid>
          ) : (
            <Box pt={2} pb={1}>
              {!overrideConfirm && !resolveOverride && (
                <>
                  <Stack direction="row" spacing={1} alignItems="baseline">
                    <Button
                      variant="text"
                      title="Override"
                      className={isAddOverrideDisabled ? classes.expressModeBtn : classes.activeBtn}
                      sx={{ paddingTop: '0px !important' }}
                      onClick={handleAddOverrideClick}
                    >
                      Add override
                    </Button>
                    <Button
                      variant="text"
                      title="Remove override"
                      className={isRemoveOverrideDisabled ? classes.expressModeBtn : classes.activeBtn}
                      onClick={handleRemoveOverrideClick}
                      sx={{ paddingTop: '0px !important' }}
                    >
                      Remove override
                    </Button>
                  </Stack>

                  <Box sx={{ maxHeight: '15rem', overflowY: 'auto' }}>
                    {Object.keys(refData).map((r: any, key: any) => (
                      <Box p={1} key={`resolve-override-${key}`}>
                        <Typography className={classes.wrapNote} variant="subtitle2">
                          <small>{r}: {refData[r]?.text}</small>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          <small>
                            by {refData[r]?.author?.name}
                            {refData[r]?.overridden_on !== undefined
                              ? ` ${moment.unix(refData[r].overridden_on).format('MMM DD, YYYY hh:mm a')}`
                              : ''}
                          </small>
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </>
              )}

              {resolveOverride && (
                <Box pb={1}>
                  <Typography pb={1} color="textSecondary" variant="subtitle2" className={classes.desc}>
                    {`Select program${Helper.isAIDA() ? '' : 's'} to resolve override`}
                  </Typography>
                  <FormControl className={classes.rrdFormControl}>
                    <Select
                      displayEmpty
                      variant="standard"
                      multiple
                      className={`${classes.selectEmpty} ${classes.desc}`}
                      inputProps={{ 'aria-label': 'resolveOverridenPrg' }}
                      label="Program"
                      value={resolveOverridenPrg}
                      name="resolveOverridenPrg"
                      renderValue={(selected) => (selected as string[]).join(', ')}
                      onChange={(e: any) => handleChangeForPrograms(e, true)}
                    >
                      {input?.overridden_programs?.map((_key: any) => (
                        <MenuItem
                          key={_key}
                          className={classes.zeroPadding}
                          value={_key}
                          sx={{ '& .MuiTypography-root': { fontSize: '0.7rem !important' } }}
                          disabled={input?.overridden_programs?.length === 1}
                        >
                          <Checkbox
                            color="primary"
                            sx={{ transform: 'scale(0.6) !important' }}
                            className={classes.zeroPadding}
                            checked={resolveOverridenPrg.indexOf(_key) > -1}
                          />
                          <ListItemText primary={getPrgName(_key)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {overrideConfirm && (
                <>
                  <Box mt={1}>
                    <TextField
                      id="outlined-basic"
                      variant="outlined"
                      label="Add note for overriding"
                      size="small"
                      className={`${classes.fullWidth} ${classes.desc}`}
                      inputProps={{ style: { fontSize: '0.7rem' } }}
                      InputLabelProps={{ style: { fontSize: '0.7rem' } }}
                      multiline
                      minRows={3}
                      value={note}
                      onChange={handleChange}
                    />
                  </Box>
                  <Box mt={1}>
                    <Grid container spacing={1} direction="row" className={classes.compactCardContent}>
                      <Grid item>
                        <Typography color="textSecondary" variant="subtitle2" className={classes.desc}>
                          {`Select program${Helper.isAIDA() ? '' : 's'}`}
                        </Typography>
                        <FormControl className={classes.rrdFormControl}>
                          <Select
                            displayEmpty
                            variant="standard"
                            multiple
                            className={classes.selectEmpty}
                            inputProps={{ 'aria-label': 'Without label' }}
                            label="Program"
                            value={overridenPrograms}
                            name="program_name"
                            renderValue={(selected) => (selected as string[]).join(', ')}
                            onChange={(e: any) => handleChangeForPrograms(e, false)}
                          >
                            {programs?.map((_key: any) => {
                              const isPrevOverridden = prevOverridenPrograms.indexOf(_key) > -1
                              const isChecked = isPrevOverridden || programs?.length === 1 || overridenPrograms.indexOf(_key) > -1

                              return (
                                <MenuItem
                                  key={_key}
                                  className={classes.zeroPadding}
                                  disabled={isPrevOverridden}
                                  value={_key}
                                  sx={{ '& .MuiTypography-root': { fontSize: '0.7rem !important' } }}
                                >
                                  <Checkbox
                                    color="primary"
                                    sx={{ transform: 'scale(0.6) !important' }}
                                    className={classes.zeroPadding}
                                    disabled={isPrevOverridden}
                                    checked={isChecked}
                                  />
                                  <ListItemText primary={getPrgName(_key)} />
                                </MenuItem>
                              )
                            })}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Box>
                </>
              )}
            </Box>
          )}
        </CardContent>

        <Grid xs={12} direction="row" justifyContent="space-around" alignItems="center">
          <ButtonGroup className={classes.questionButtonStyle} size="small" variant="text" aria-label="button group">
            <Button
              disabled={isSubmitDisabled}
              onClick={onConfirm}
              className={classes.twoButtonWidth}
            >
              <strong>Submit</strong>
            </Button>
            <Button onClick={onCloseOverride} className={classes.twoButtonWidth}>
              <strong>Cancel</strong>
            </Button>
          </ButtonGroup>
        </Grid>
      </Card>
    </Box>
  )
}

export default withStyles(stylesheet)(ManualOverrideComponent)